import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Award, FileText, Palette, Shield } from 'lucide-react';
import { CertificationDocumentViewer } from './CertificationDocumentViewer';
import { RoleType, AgreementType } from '@/services/certificationAgreementService';

interface CertificationAgreementsModalProps {
  open: boolean;
  roleType: RoleType;
  onSubmit: () => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const roleTypeLabels: Record<RoleType, string> = {
  team_owner: 'Team Owner',
  coach: 'Coach',
  venue: 'Venue Operator'
};

export const CertificationAgreementsModal = ({
  open,
  roleType,
  onSubmit,
  onCancel,
  isSubmitting = false
}: CertificationAgreementsModalProps) => {
  const [platformParticipationAccepted, setPlatformParticipationAccepted] = useState(false);
  const [brandUsageAccepted, setBrandUsageAccepted] = useState(false);
  const [certificationTermsAccepted, setCertificationTermsAccepted] = useState(false);
  
  const [viewingAgreement, setViewingAgreement] = useState<AgreementType | null>(null);

  const allAccepted = platformParticipationAccepted && brandUsageAccepted && certificationTermsAccepted;

  const handleSubmit = async () => {
    if (!allAccepted) return;
    await onSubmit();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent 
          className="max-w-2xl max-h-[90vh] overflow-hidden bg-card border-border"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl md:text-2xl font-bold text-foreground flex items-center gap-2">
              <Award className="h-5 w-5 md:h-6 md:w-6 text-primary" />
              Certified Role Participation Agreements
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              As a {roleTypeLabels[roleType]}, you must agree to the following participation agreements 
              to activate your certified role on Players Lockeroom.
            </DialogDescription>
          </DialogHeader>

          {/* Info Banner */}
          <div className="flex items-start gap-3 p-4 bg-primary/10 border border-primary/30 rounded-lg">
            <Shield className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-foreground">What This Means</p>
              <p className="text-muted-foreground mt-1">
                These agreements define your participation rights, brand usage guidelines, and certification 
                standards as a certified participant on the Players Lockeroom platform.
              </p>
            </div>
          </div>

          <ScrollArea className="max-h-[calc(85vh-320px)] pr-4 flex-1">
            <div className="space-y-4">
              {/* Platform Participation Agreement */}
              <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="platform-participation"
                    checked={platformParticipationAccepted}
                    onCheckedChange={(checked) => setPlatformParticipationAccepted(checked as boolean)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="platform-participation" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      Platform Participation Agreement
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={() => setViewingAgreement('platform_participation')}
                        className="text-primary hover:underline"
                      >
                        Platform Participation Agreement
                      </button>
                      , which defines the nature of participation, certified roles, platform identity, 
                      data usage, fees, and termination terms.
                    </p>
                  </div>
                </div>
              </div>

              {/* Brand Usage Guidelines */}
              <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="brand-usage"
                    checked={brandUsageAccepted}
                    onCheckedChange={(checked) => setBrandUsageAccepted(checked as boolean)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="brand-usage" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2">
                      <Palette className="h-4 w-4 text-primary" />
                      Brand Usage Guidelines
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={() => setViewingAgreement('brand_usage')}
                        className="text-primary hover:underline"
                      >
                        Brand Usage Guidelines
                      </button>
                      , which govern the use of logos, badges, co-branding, and protect Players Lockeroom's identity.
                    </p>
                  </div>
                </div>
              </div>

              {/* Certification Terms */}
              <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="certification-terms"
                    checked={certificationTermsAccepted}
                    onCheckedChange={(checked) => setCertificationTermsAccepted(checked as boolean)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="certification-terms" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2">
                      <Award className="h-4 w-4 text-primary" />
                      Certification Terms
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={() => setViewingAgreement('certification_terms')}
                        className="text-primary hover:underline"
                      >
                        Certification Terms
                      </button>
                      , which define certification meaning, trust verification, rights granted, revocation 
                      conditions, and outcome disclaimers.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>

          <div className="flex-shrink-0 flex justify-between gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!allAccepted || isSubmitting}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {isSubmitting ? 'Processing...' : 'I Agree - Continue'}
            </Button>
          </div>

          <div className="flex-shrink-0 text-xs text-muted-foreground text-center">
            By clicking "I Agree - Continue", you acknowledge that you have read, understood, and agree 
            to be bound by all three agreements as a certified {roleTypeLabels[roleType]} on Players Lockeroom.
          </div>
        </DialogContent>
      </Dialog>

      {/* Agreement Viewer Dialog */}
      {viewingAgreement && (
        <CertificationDocumentViewer
          agreementType={viewingAgreement}
          open={!!viewingAgreement}
          onClose={() => setViewingAgreement(null)}
        />
      )}
    </>
  );
};
